import { Actions, ofType, createEffect } from '@ngrx/effects';
import { catchError, map, tap, take, exhaustMap } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { User } from '../user.model';
import { RequestHelpersService } from 'src/app/shared/services/request-helpers.service';
import { useUserStore } from '../../store/user.store';
import { UserProfileService } from 'src/app/user-profile/user-profile.service';
import { useToastStore } from 'src/app/store/toast.store';
import { LocalStorageKeys } from 'src/app/constants/storageKeys';
import { VerifyEmailService } from 'src/app/services/verify-email-service/verify-email.api.service';
import { useModalManagerStore } from 'src/app/store/modal-manager.store';
import { AuthService, SignInCredentials } from '../services/auth-service.service';
import { helpContent } from 'src/app/constants/constants';
import * as AuthActions from './auth.actions';

const handleSignUpSuccess = (userCreds: SignInCredentials) => {
  return AuthActions.loginStart(userCreds);
};

/**
 * Signup-specific status messages.
 *
 * 403 means something different on /auth/signup than everywhere else: the API
 * returns it when the Turnstile bot check fails, not when an authenticated
 * client lacks access. Telling a would-be customer they are "not authorized to
 * access this resource" because a challenge misfired is both wrong and
 * unrecoverable for them. 429 and 503 are new from the same change.
 */
const signUpErrorMessage = (status: number): string | null => {
  switch (status) {
    case 403:
      return 'We could not verify that this request came from a browser. Please complete the challenge and try again.';
    case 429:
      return 'Too many attempts. Please wait a few minutes and try again.';
    case 503:
      return 'Signups are temporarily unavailable. Please try again in a little while.';
    default:
      return null;
  }
};

const handleError = (errorRes: any, isSignUp = false) => {
  localStorage.removeItem(LocalStorageKeys.CLIENT_JWT);
  let errorMessage = helpContent.GENERAL_ERROR;
  if (!errorRes.status || !errorRes.error) {
    useToastStore.setState({
      isVisible: true,
      content: `${errorMessage}`,
      variant: 'danger',
    });
    // of() is a utility function for creating a new obserable without an error
    return of(AuthActions.authenticateFail(errorMessage));
  }

  const signUpSpecific = isSignUp ? signUpErrorMessage(errorRes.status) : null;
  if (signUpSpecific) {
    useToastStore.setState({
      isVisible: true,
      content: `${signUpSpecific}`,
      variant: 'danger',
    });
    return of(AuthActions.authenticateFail(signUpSpecific));
  }

  switch (errorRes.status) {
    case 403:
      errorMessage = 'Client is not authorized to access this resource.';
      break;
    case 401:
      errorMessage = 'Email or password is incorrect.';
      break;
    case 409:
      errorMessage = 'Email already exists';
      break;
    default:
      errorMessage;
  }
  useToastStore.setState({
    isVisible: true,
    content: `${errorMessage}`,
    variant: 'danger',
  });
  return of(AuthActions.authenticateFail(errorMessage));
};

@Injectable()
export class AuthEffects {
  private setUser = useUserStore.getState().setUser;
  private resetUser = useUserStore.getState().resetUser;
  private isEmailVerified: boolean;
  private verifyEmailSubscription$: Subscription;
  private modalManagerStore = useModalManagerStore.getState;

  authSignup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.signUpStart),
      exhaustMap((action) =>
        this.authService.authSignUp(action).pipe(
          tap((resData) => {
            this.setUser(resData);
          }),
          map((resData: any) => {
            return handleSignUpSuccess({
              email: action.email,
              password: action.password,
            });
          }),
          catchError((errorRes) => {
            return handleError(errorRes, true);
          })
        )
      )
    )
  );

  authLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginStart),
      exhaustMap((action) =>
        this.authService.authLogin(action).pipe(
          tap((resData) => {
            // this.setUser(resData);
          }),
          map((resData: any) => {
            return this.handleAuthentication(resData.access_token);
          }),
          catchError((errorRes) => {
            return handleError(errorRes);
          })
        )
      )
    )
  );

  autoLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.autoLogin),
      map(() => {
        const userData: {
          _token: string;
        } = JSON.parse(localStorage.getItem(LocalStorageKeys.CLIENT_JWT));
        if (!userData) {
          return { type: 'NO_USER_DATA' };
        }
        if (this.requestHelpers.isJwtExpired()) {
          localStorage.removeItem(LocalStorageKeys.CLIENT_JWT);
          this.router.navigate(['/auth']);
        } else {
          this.handleAuthentication(userData._token);
          return AuthActions.authenticateSuccess({
            token: userData._token,
            redirect: false,
          });
        }
        return { type: 'USER' };
      })
    )
  );

  autoLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logOut),
        tap(() => {
          this.resetUser();
          localStorage.removeItem(LocalStorageKeys.CLIENT_JWT);
          this.router.navigate(['/auth']);
        })
      ),
    { dispatch: false }
  );

  authRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.authenticateSuccess),
        tap((authSuccessAction) => {
          if (authSuccessAction.redirect) {
            if (this.router.routerState.snapshot.url.includes('registration'))
              this.router.navigate(['/billing-plans']);
            else this.router.navigate(['/']);
          }
        })
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private router: Router,
    private requestHelpers: RequestHelpersService,
    private userService: UserProfileService,
    private verifyEmailService: VerifyEmailService,
    private authService: AuthService
  ) { }

  handleAuthentication = (token: string) => {
    const asyncFunc = async () => {
      localStorage.removeItem(LocalStorageKeys.CLIENT_JWT);
      const user = new User(token);
      localStorage.setItem(LocalStorageKeys.CLIENT_JWT, JSON.stringify(user));
      const userProfile = await this.userService.getUserProfile();
      useUserStore.setState({ ...userProfile });
      if (token) this.openEmailVerifyModal();
    };

    asyncFunc();

    return AuthActions.authenticateSuccess({
      token: token,
      redirect: true,
    });
  };

  openEmailVerifyModal = () => {
    this.userService.getUserProfile();
    const isEmailVerified = useUserStore.getState().email_verified;
    this.isEmailVerified = isEmailVerified;
    if (!isEmailVerified) {
      const emailVerifySub$ = this.verifyEmailService.sendVerifyCode();
      emailVerifySub$.pipe(take(1)).subscribe((res: any) => {
        if (res?.message !== 'Success') {
          this.modalManagerStore().toggleModal('emailVerify', false);
        }
      });
    }
  };
}
